import { useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { SiteSelect } from '@/components/common';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { Input } from '@/components/ui/input';
import { useCurrentUser } from '@/services/auth';
import { formatURLDate } from '@/utils';

import {
  DATE_PICKER_MATCH,
  hardcodedSites,
  MOBILE_BREAKPOINT,
  TRANS_OVA_GENETICS_COMPANY_ID,
} from '../utils/constants';

type FreezeSelectorProps = {
  quickSearchValue: string;
  setQuickSearchValue: (value: string) => void;
};

export const FreezeSelector = ({
  quickSearchValue,
  setQuickSearchValue,
}: FreezeSelectorProps) => {
  const [params, setParams] = useSearchParams();
  const fertilizationDate = params.get('date');
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  useEffect(() => {
    if (!params.get('date')) {
      const newParams = new URLSearchParams(params);
      newParams.set('date', formatURLDate(new Date()));
      setParams(newParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const user = useCurrentUser();
  const company = user.data?.companies.find((c) => c.isActive);
  const isTransOvaGeneticsGroup =
    company?.companyId === TRANS_OVA_GENETICS_COMPANY_ID;
  const hardcodedOptions = isTransOvaGeneticsGroup
    ? hardcodedSites.map((s) => ({ id: s.value, name: s.label }))
    : undefined;

  const handleDateChange = useCallback(
    (date: Date | null) => {
      const newParams = new URLSearchParams(params);
      if (date) {
        newParams.set('date', formatURLDate(date));
      } else {
        newParams.delete('date');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const [search, setSearch] = useState(quickSearchValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setQuickSearchValue(value);
    }, 500),
    [setQuickSearchValue],
  );

  return (
    <div
      className={`flex gap-2 ${isMobile ? 'flex-col' : 'flex-row items-center'}`}
    >
      <DatePickerInput
        initialDate={
          fertilizationDate ? new Date(fertilizationDate) : undefined
        }
        placeholder="Fertilization Date"
        onChange={handleDateChange}
        className={`${DATE_PICKER_MATCH} ${isMobile ? 'w-full' : 'w-auto min-w-44'}`}
      />

      <SiteSelect
        paramKey="sId"
        filter="fertilization"
        extraOptions={hardcodedOptions}
        placeholder="Fertilization Site"
        disabled={!fertilizationDate || !!search.length}
        triggerClassName={isMobile ? 'w-full' : 'min-w-48'}
      />

      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
        <Input
          placeholder="Search matings..."
          disabled={!fertilizationDate}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            debouncedSetSearch(e.target.value);
          }}
          className={`pl-8 ${isMobile ? 'w-full' : 'w-auto min-w-36'}`}
        />
      </div>
    </div>
  );
};
